import { Box, Container, Heading, Text, Icon, Tag } from '@everlywell/ui-kit';
import { Check, CheckCircle } from '@phosphor-icons/react';
import React from 'react';
import renderRichText, {
  ContentfulRichText,
} from 'utils/contentful/renderRichText';

// Types ----------

export interface PricingCardProps {
  handleClick: (index: number) => void;
  index: number;
  isSelected: boolean;
  period: string;
  description: ContentfulRichText;
  url: string;
  price: {
    value: string;
    discount: string;
    frequency: string;
  };
  perks: {
    title: string;
    isHighlighted: boolean;
  }[];
  isStatic?: boolean;
  onlyChild?: boolean;
  isInvalid?: boolean;
}

// PricingCard ------
const PricingCard: React.FC<PricingCardProps> = (props) => {
  const {
    description,
    handleClick,
    index,
    isSelected,
    onlyChild = false,
    period,
    perks,
    price,
    isStatic = false,
  } = props;

  const priceSections = price?.value?.split('.');

  const perkList =
    perks &&
    perks.map((perk, index: number) => (
      <Box
        key={`perk-${index}`}
        display="flex"
        alignItems="center"
        width="100%"
        marginBottom={2}
        _last={{ marginBottom: 0 }}
      >
        <Icon
          as={CheckCircle}
          size="md"
          color={perk.isHighlighted ? 'eggplant.base' : 'viridian.base'}
          marginRight={[1, 2]}
        />
        <Text
          size="md"
          color={perk.isHighlighted ? 'eggplant.base' : 'tints.black'}
        >
          {perk.title}
        </Text>
      </Box>
    ));

  return (
    <Container
      _hover={{ cursor: isStatic ? 'default' : 'pointer' }}
      backgroundColor="tints.white"
      border={isStatic ? '0' : '2px'}
      borderColor={isSelected ? 'viridian.base' : 'tints.white'}
      borderRadius="12"
      data-testid="pricing-card"
      height="100%"
      onClick={() => handleClick(index)}
      padding={[6, 10]}
      position="relative"
      maxWidth={onlyChild ? '478px' : 'unset'}
      size="fluid"
    >
      {!isStatic && (
        <Box
          width="32px"
          height="32px"
          borderRadius="50%"
          border="1px"
          borderColor={isSelected ? 'viridian.base' : 'tints.uiGrey'}
          backgroundColor={isSelected ? 'viridian.base' : 'tints.white'}
          position="absolute"
          top={[6, 10]}
          right={[6, 10]}
        >
          <Icon
            as={Check}
            color="tints.white"
            width="100%"
            height="100%"
            padding="12%"
          />
        </Box>
      )}
      <Heading size="md" marginBottom={2}>
        {period}
      </Heading>

      <Text as="div" size="lg" color="tints.darkGrey" marginBottom={5}>
        {description ? renderRichText(description) : null}
      </Text>

      <Box display="flex" alignItems="center" width="100%">
        {priceSections?.[0] ? (
          <Box
            marginBottom="2px"
            display="flex"
            alignItems="baseline"
            marginRight={['10px', 3]}
          >
            <Heading size="md">$</Heading>
            <Heading size="lg">{priceSections?.[0]}</Heading>
            {priceSections?.[1] ? (
              <Heading size="md">{`.${priceSections?.[1]}`}</Heading>
            ) : null}
          </Box>
        ) : null}

        {price?.discount ? (
          <Tag
            size="sm"
            variant="tertiary"
            padding={['4px 8px', 2]}
            background="eggplant.base"
          >
            {price.discount}
          </Tag>
        ) : null}
      </Box>

      {price?.frequency ? (
        <Text size="md" color="tints.darkGrey" marginBottom={[6, 8]}>
          {price.frequency}
        </Text>
      ) : null}

      <Box
        width="100%"
        height="1px"
        backgroundColor="tints.uiGrey"
        marginBottom={[6, 8]}
      />

      <Box>{perkList}</Box>
    </Container>
  );
};

// Export ---------

export default React.memo(PricingCard);
